import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useLinkUser, useSetClientPassword, useCreatePortalInvite } from '../contexts/clients';

import './styles/portal-invite.css';

const portalBase = () => (import.meta.env.VITE_APP_CLIENT || '').replace(/\/$/, '');

// Muestra un enlace de invitación (registro/reset) con copiar.
function InviteLink({ invite }) {
    const base = portalBase();
    const full = base ? base + invite.link : invite.link;
    const copy = async () => {
        try { await navigator.clipboard.writeText(full); toast.success('Enlace copiado'); }
        catch { toast.error('No se pudo copiar'); }
    };
    return (
        <div className="cli-invite__body">
            <p className="cli-invite__hint">Usuario creado. Compartí este enlace para que el cliente ponga su contraseña y pueda ingresar:</p>
            <div className="cli-invite__linkbox">
                <code className="cli-invite__link">{full}</code>
                <button type="button" className="cli-invite__btn cli-invite__btn--ghost" onClick={copy}>Copiar</button>
            </div>
            {!base && <p className="cli-invite__note">Enlace relativo. Configurá <code>VITE_APP_CLIENT</code> para la URL completa.</p>}
            <p className="cli-invite__note">Vence el {new Date(invite.expires_at).toLocaleString('es-CO')}. Un solo uso.</p>
        </div>
    );
}

// Usuario y acceso del cliente (admin-clients.md · 0046 + link-user/set-password).
// - Cliente SIN usuario → crea el usuario (rol owner) y lo vincula (link-user). Con contraseña
//   (≥10) nace usable (login inmediato); sin contraseña, emite enlace de registro.
// - Cliente CON usuario → asignar/restablecer contraseña (set-password, revoca sesiones) o
//   generar un enlace de restablecimiento (portal-invite).
export default function ClientUserForm({ client, onClose }) {
    const hasUser = !!client.user_id;

    const { mutateAsync: linkUser, isPending: linking } = useLinkUser(client.id);
    const { mutateAsync: setPassword, isPending: settingPw } = useSetClientPassword(client.id);
    const { mutateAsync: invite, isPending: inviting } = useCreatePortalInvite(client.id);
    const busy = linking || settingPw || inviting;
    const cardRef = useModalDialog(onClose);

    // crear usuario (sin user)
    const [email, setEmail] = useState(client.email || '');
    const [username, setUsername] = useState('');
    const [name, setName] = useState(client.name || '');
    const [password, setPassword_] = useState('');
    const [result, setResult] = useState(null); // { invite } | { password_set } (link-user)
    // reset (con user)
    const [pw, setPw] = useState('');
    const [resetInvite, setResetInvite] = useState(null);

    const createUser = async () => {
        if (!client.email && !email.trim()) { toast.error('El correo es obligatorio (el cliente no tiene)'); return; }
        if (password && password.length < 10) { toast.error('La contraseña debe tener al menos 10 caracteres'); return; }
        const payload = {};
        if (email.trim()) payload.email = email.trim().toLowerCase();
        if (username.trim()) payload.username = username.trim();
        if (name.trim()) payload.name = name.trim();
        if (password) payload.password = password;
        try {
            const res = await linkUser(payload);
            setResult(res);
            toast.success(res?.password_set ? 'Usuario creado con contraseña' : 'Usuario creado');
        } catch (er) {
            if (er?.status === 409) toast.error('El cliente ya tiene usuario, o el correo/usuario ya está en uso');
            else if (er?.status === 400) toast.error('Correo requerido/ inválido o contraseña < 10');
            else if (er?.status === 429) toast.error('Límite alcanzado (3 cada 24 h). Probá más tarde.');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo crear el usuario');
        }
    };

    const doSetPassword = async () => {
        if (pw.length < 10) { toast.error('La contraseña debe tener al menos 10 caracteres'); return; }
        try { await setPassword(pw); setPw(''); toast.success('Contraseña asignada. Se revocaron las sesiones del cliente.'); }
        catch (er) {
            if (er?.status === 409) toast.error('El cliente no tiene usuario (usá “Crear usuario”)');
            else if (er?.status === 400) toast.error('La contraseña debe tener al menos 10 caracteres');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo asignar la contraseña');
        }
    };

    const genReset = async () => {
        try { const inv = await invite(); setResetInvite(inv); toast.success('Enlace generado'); }
        catch (er) {
            if (er?.status === 429) toast.error('Límite alcanzado (3 cada 24 h). Probá más tarde.');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo generar el enlace');
        }
    };

    return (
        <div className="cli-invite" role="dialog" aria-modal="true" aria-label="Usuario y acceso del cliente">
            <div className="cli-invite__overlay" onClick={onClose} aria-hidden="true" />
            <div className="cli-invite__card" ref={cardRef}>
                <header className="cli-invite__head">
                    <div>
                        <h2 className="cli-invite__title">Usuario y acceso</h2>
                        <span className="cli-invite__sub">{client.name}</span>
                    </div>
                    <button type="button" className="cli-invite__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                {!hasUser ? (
                    result ? (
                        result.invite ? <InviteLink invite={result.invite} /> : (
                            <div className="cli-invite__body"><p className="cli-invite__hint">Usuario creado con la contraseña asignada. El cliente ya puede ingresar al portal.</p></div>
                        )
                    ) : (
                        <div className="cli-invite__body">
                            <p className="cli-invite__hint">Este cliente no tiene usuario. Creá su acceso al portal.</p>
                            {!client.email && (
                                <label className="cli-invite__field">
                                    <span className="cli-invite__flabel">Correo (obligatorio)</span>
                                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} placeholder="cliente@dominio.co" />
                                </label>
                            )}
                            {client.email && <p className="cli-invite__note">El usuario usará el correo del cliente: <strong>{client.email}</strong></p>}
                            <label className="cli-invite__field">
                                <span className="cli-invite__flabel">Usuario <em>(opcional)</em></span>
                                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} disabled={busy} placeholder="handle de login" />
                            </label>
                            <label className="cli-invite__field">
                                <span className="cli-invite__flabel">Nombre <em>(opcional)</em></span>
                                <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
                            </label>
                            <label className="cli-invite__field">
                                <span className="cli-invite__flabel">Contraseña <em>(opcional, ≥10)</em></span>
                                <input type="password" value={password} onChange={(e) => setPassword_(e.target.value)} disabled={busy} autoComplete="new-password" placeholder="Vacío = enviar enlace de registro" />
                            </label>
                            <p className="cli-invite__note">Con contraseña, el cliente ingresa de inmediato. Sin contraseña, se genera un enlace de registro para que ponga la suya.</p>
                            <button type="button" className="cli-invite__btn cli-invite__btn--primary" onClick={createUser} disabled={busy}>{linking ? 'Creando…' : 'Crear usuario'}</button>
                        </div>
                    )
                ) : (
                    <div className="cli-invite__body">
                        <p className="cli-invite__hint">Este cliente ya tiene usuario. Podés asignarle una contraseña nueva o enviarle un enlace de restablecimiento.</p>
                        <label className="cli-invite__field">
                            <span className="cli-invite__flabel">Nueva contraseña <em>(≥10)</em></span>
                            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} disabled={busy} autoComplete="new-password" />
                        </label>
                        <p className="cli-invite__note">Asignar una contraseña revoca las sesiones activas del cliente.</p>
                        <button type="button" className="cli-invite__btn cli-invite__btn--primary" onClick={doSetPassword} disabled={busy || pw.length < 10}>{settingPw ? 'Asignando…' : 'Asignar contraseña'}</button>

                        <hr className="cli-invite__sep" />
                        {resetInvite ? <InviteLink invite={resetInvite} /> : (
                            <button type="button" className="cli-invite__btn cli-invite__btn--ghost" onClick={genReset} disabled={busy}>{inviting ? 'Generando…' : 'Generar enlace de restablecimiento'}</button>
                        )}
                    </div>
                )}

                <footer className="cli-invite__foot">
                    <button type="button" className="cli-invite__btn cli-invite__btn--ghost" onClick={onClose}>Cerrar</button>
                </footer>
            </div>
        </div>
    );
}
